import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import isEmpty from 'lodash-es/isEmpty';
import { combineLatest, EMPTY, forkJoin, map, of, TimeoutError } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, finalize, switchMap, take, tap, timeout, withLatestFrom } from 'rxjs/operators';
import { ContractFilterStatus } from '../../../enums/contract-filter-status.enum';
import { ContractStatusEnum } from '../../../enums/contract-status.enum';
import { ApiHeaders } from '../../../models/api-query.model';
import { ContractConversionDto, ContractCP, ContractDetail } from '../../../models/contract-model';
import { LoggerService, ModalService, NotificationService } from '../../../services';
import { AppFacadeService } from '../../../services/app-facade.service';
import { ConsumerPortalApiService } from '../../../services/consumer-portal-api.service';
import { ContractService } from '../../../services/contract.service';
import { QuoteStepService } from '../../../services/quote-step.service';
import { selectCustomerId, selectRouteParamsContractCustomerId, selectRouteParamsContractIds } from '../../app-wide/config/config.selectors';
import { setContractCurrency } from '../../app-wide/currencycode/currencycode.actions';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';
import {
    clearContractinStore,
    ContractActionTypes,
    failLoadContractDetails,
    fetchContractDetail,
    fetchContracts,
    loadContractAction,
    setContractDetailData,
    setNoMoreRecord,
    setSelectedContracts,
    SetContractDetailLineItemsData,
    setContractDetailPaymentData,
    fetchContractDetailLineItems,
    fetchContractDetailPayment,
    convertQuoteToReservation,
    setQuoteConversionInProgress,
    markQuoteConverted
} from './contract.actions';
import { getPageNumber, getQuoteConversionInProgress } from './contract.selectors';
import { loadAccountFeaturesFinished } from '../feature-account/account.actions';
import { InvoiceActionTypes } from '../invoices/invoices.actions';

// Mirrors api-lib ErrorTokens.QuoteStepsIncomplete (the UI lib cannot import api-lib).
const quoteStepsIncompleteToken = 'QUOTE_STEPS_INCOMPLETE';

// openContract does not always reload the detail (e.g. QuickLink keeps the same selection), so an unconsumed
// skip must lapse rather than swallow a later user-initiated load.
const skipRecheckWindowMs = 30_000;
const convertQuoteTimeoutMs = 60_000;

@Injectable()
export class ContractEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(ModalService);
    private readonly contractService = inject(ContractService);
    private readonly notificationService = inject(NotificationService);
    private readonly quoteStepService = inject(QuoteStepService);
    private readonly logger = inject(LoggerService);
    // Per-session guard against re-converting a contract that already converted, whose GAPI eligibility check
    // definitively failed, or whose convert call timed out (outcome unknown, so the server may still be converting).
    // Never set on dispatch or on a transport/step failure: those use skipNextRecheck so a later user-initiated load can retry.
    private readonly autoConvertAttempted = new Set<string>();
    // A failed conversion reopens the contract; that reload must not re-dispatch the conversion, or it loops.
    // Maps the id to an expiry; consumed by the next detail load for the id, or ignored once expired.
    private readonly skipNextRecheck = new Map<string, number>();

    onPaymentOpen$ = createEffect(
        () =>
            this.actions$.pipe(
                // ADO-116163: open the shared POR Pay modal for the invoice multipay continue too.
                ofType(ContractActionTypes.MultiPaymentContinue, InvoiceActionTypes.InvoiceMultiPaymentContinue),
                map(() => this.modalService.open('payment-modal'))
            ),
        { dispatch: false }
    );

    onPaymentClose$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContractActionTypes.PaymentWindowClosed),
                map(() => this.modalService.close('payment-modal'))
            ),
        { dispatch: false }
    );

    /**
     * fetchContracts$ (which clears the app loader once contracts return) is skipped when the user
     * lacks Account.ViewContracts, and in Basic version the contracts view is never rendered to
     * dispatch fetchContracts at all. Clear the loader once permissions resolve so the loader that
     * was set while the portal initialised does not stay visible over the no-permission message.
     */
    clearLoaderWhenContractsNotViewable$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadAccountFeaturesFinished),
                filter(() => !this.appFacadeService.featureToggleService.isAvailable('Account.ViewContracts')),
                tap(() => this.appFacadeService.setLoading(false))
            ),
        { dispatch: false }
    );

    fetchContracts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContracts),
            filter(() => this.appFacadeService.featureToggleService.isAvailable('Account.ViewContracts')),
            tap(() => this.appFacadeService.setLoading(true)),
            withLatestFrom(this.store.pipe(select(getPageNumber)), this.appFacadeService.getPageSizeValues()),
            switchMap(([action, currentPageNumber, pageSizeValues]) => {
                const { selectedPageSize, previousPageSize } = pageSizeValues;

                const { customerId, status = [], startDate, endDate, search } = action;
                let pageNumber = currentPageNumber;
                if (previousPageSize !== selectedPageSize) {
                    this.store.dispatch(clearContractinStore());
                    pageNumber = 1;
                }
                const headers: ApiHeaders = this.prepareHeaders(pageNumber, customerId, status, startDate, endDate, search, Number(selectedPageSize));

                return this.consumerPortalApi.get<ContractCP>(`customer/${customerId}/contracts`, { headers }).pipe(
                    switchMap((contracts: ContractCP[]) => {
                        if (!contracts || contracts.length === 0) {
                            return of(setNoMoreRecord({ record: true }));
                        }

                        /**
                         * Don't try to translate original status
                         */
                        // contracts = contracts.map((contract: ContractCP) => ({
                        //     ...contract,
                        //     status: this.translateService.instant(contract.status)
                        // }));

                        return of(loadContractAction({ contracts, pageSize: Number(selectedPageSize) }), setContractCurrency({ currencyCode: contracts[0]?.CurrencyCode || '' }));
                    }),
                    catchError(() => of(setNoMoreRecord({ record: true }))),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    fetchContractDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetail),
            tap(() => this.appFacadeService.setLoading(true)),
            switchMap(({ forPayment, customerId, contractId }) =>
                combineLatest([this.store.pipe(select(selectRouteParamsContractCustomerId)), this.store.pipe(select(selectRouteParamsContractIds))]).pipe(
                    take(1),
                    map(([customerIdFromState, contractIdsFromState]) => ({
                        customerId: customerId ?? customerIdFromState,
                        contractIds: contractId ? [contractId] : contractIdsFromState,
                        forPayment
                    }))
                )
            ),
            switchMap(({ forPayment, customerId, contractIds }) => {
                if (!customerId || !contractIds || contractIds.length === 0) {
                    return of(failLoadContractDetails({ error: 'Missing customerId or contractIds' }));
                }

                // Fetch all contracts in parallel
                const fetchObservables = contractIds.map(id => this.contractService.getContractsDetails(customerId, id));

                return forkJoin(fetchObservables).pipe(
                    concatMap((detailsArray: ContractDetail[][]) => {
                        // Null-safety: Filter out empty/null responses
                        const validDetails = detailsArray.filter(details => details && details.length > 0);

                        if (validDetails.length === 0) {
                            this.notificationService.error(this.translateService.instant('NoContractDetailsFound'));
                            return of(failLoadContractDetails({ error: 'No contract details found' }));
                        }

                        const contracts: ContractCP[] = validDetails.map(details => this.contractService.convertContractDetailToContractCP(details[0]));

                        const actions = [];
                        actions.push(loadContractAction({ contracts, showInList: false }));

                        if (forPayment) {
                            const fetchedContractIds = contracts.map(c => c.contractId);
                            actions.push(setSelectedContracts({ selectedContracts: fetchedContractIds }));
                        }

                        // Set first contract detail (legacy single-contract behavior)
                        actions.push(setContractDetailData({ data: validDetails[0][0] }));

                        return of(...actions);
                    }),
                    catchError((error: HttpErrorResponse) => of(failLoadContractDetails({ error: error?.message }))),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                );
            })
        )
    );

    loadLineItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetailLineItems),
            switchMap(({ customerId, contractId }) =>
                this.contractService.getContractsDetailsLineItems(customerId, contractId).pipe(
                    map(lineItems => SetContractDetailLineItemsData({ data: lineItems })),
                    catchError(() => EMPTY) // optional: silently fail
                )
            )
        )
    );

    loadPaymentHistory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchContractDetailPayment),
            switchMap(({ customerId, contractId }) => {
                if (!this.appFacadeService.featureToggleService.isAvailable('Account.PaymentHistory')) {
                    return EMPTY;
                }
                return this.contractService.getContractsDetailsPaymentHistory(customerId, contractId).pipe(
                    map(payments => setContractDetailPaymentData({ data: payments })),
                    catchError(() => EMPTY)
                );
            })
        )
    );

    /**
     * Reservation-step-tracker AC8/AC9: recheck "are all required steps complete" every time a Quote's
     * detail is (re)loaded — not just after a payment success — since eSign is an external full-page
     * redirect with no in-app completion event; the consumer returning to the app and the detail
     * reloading is the only signal we get. `setContractDetailData` is the universal hook: it fires both
     * from the normal fetchContractDetail$ effect and from QuickLink's direct dispatch after an eSign
     * redirect (quick-link.component.ts -> appFacadeService.setContractDetail).
     */
    recheckQuoteCompletionOnDetailLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setContractDetailData),
            filter(({ data }) => (data.Status ?? data.ContractStatus) === ContractStatusEnum.Quote),
            filter(() => this.appFacadeService.featureToggleService.isAvailable('autoConvertQuotes')),
            withLatestFrom(this.store.pipe(select(getQuoteConversionInProgress)), this.store.pipe(select(selectCustomerId))),
            filter(([, inProgress]) => !inProgress),
            // QuickLink detail data (QuickLinkMapping.Contract) has no CustomerId and sets no route
            // param, so the session customer id is the only source there. selectCustomerId yields '0' when unset.
            filter(([{ data }, , sessionCustomerId]) => !!(this.usableCustomerId(sessionCustomerId) || data.CustomerId)),
            concatMap(([{ data }, , sessionCustomerId]) => {
                const customerId = this.usableCustomerId(sessionCustomerId) || data.CustomerId || '';
                const contractId = data.Id;
                const skipUntil = this.skipNextRecheck.get(contractId);
                this.skipNextRecheck.delete(contractId);
                if (skipUntil !== undefined && Date.now() < skipUntil) {
                    return EMPTY;
                }
                return this.quoteStepService.resolveStepStatus(customerId, contractId, data).pipe(
                    filter(status => status.shouldAutoConvert && !this.autoConvertAttempted.has(contractId)),
                    map(() => convertQuoteToReservation({ customerId, contractId })),
                    catchError(error => {
                        this.logger.logError(
                            JSON.stringify({
                                context: 'ContractEffects.recheckQuoteCompletionOnDetailLoad$',
                                message: 'Quote completion recheck failed',
                                customerId,
                                contractId,
                                error: error?.message,
                                status: error?.status
                            }),
                            '',
                            true
                        );
                        return EMPTY;
                    })
                );
            })
        )
    );

    convertQuoteToReservation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(convertQuoteToReservation),
            tap(() => this.store.dispatch(setQuoteConversionInProgress({ inProgress: true }))),
            withLatestFrom(this.appFacadeService.getActiveTab()),
            // exhaustMap (not switchMap): a duplicate convertQuoteToReservation dispatch while a
            // conversion is already in flight must be ignored, not cancel the in-flight GAPI call.
            exhaustMap(([{ customerId, contractId }, currentActiveTab]) => {
                if (!this.appFacadeService.featureToggleService.isAvailable('autoConvertQuotes')) {
                    this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                    this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                    this.appFacadeService.openContract(contractId);
                    return EMPTY;
                }

                this.appFacadeService.setActiveTab(ComponentsToLoad.QuoteConversion);

                return this.contractService.convertQuoteToReservation(customerId, contractId).pipe(
                    timeout(convertQuoteTimeoutMs),
                    switchMap((result: ContractConversionDto) => {
                        if (result.success && result.contract) {
                            // A late recheck carrying the pre-conversion Quote must not convert it again.
                            this.autoConvertAttempted.add(contractId);
                            this.store.dispatch(markQuoteConverted({ contractId: result.contract.Id }));
                            this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                            this.appFacadeService.openContract(result.contract.Id);

                            return of(setQuoteConversionInProgress({ inProgress: false }));
                        } else {
                            // Definitive GAPI rejection (e.g. eligibility check failed) - mark attempted so
                            // recheckQuoteCompletionOnDetailLoad$ doesn't retry it on every subsequent load.
                            this.autoConvertAttempted.add(contractId);
                            this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                            this.appFacadeService.openContract(contractId);
                            this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                            return EMPTY;
                        }
                    }),
                    catchError(error => {
                        this.logger.logError(
                            JSON.stringify({
                                context: 'ContractEffects.convertQuoteToReservation$',
                                message: 'Quote conversion failed',
                                customerId,
                                contractId,
                                error: error?.message,
                                status: error?.status,
                                token: error?.error?.token
                            }),
                            '',
                            true
                        );
                        this.skipNextRecheck.set(contractId, Date.now() + skipRecheckWindowMs);
                        // The server's step gate can disagree with a client that dispatched on stale state (or on the
                        // storefront redirect, where nothing is signed yet): show the quote, not a failure.
                        // A timeout is the same: the outcome is unknown, so the reloaded quote is the truth.
                        if (error instanceof TimeoutError) {
                            this.autoConvertAttempted.add(contractId);
                        }
                        if (error instanceof TimeoutError || error?.error?.token === quoteStepsIncompleteToken) {
                            this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
                            this.appFacadeService.openContract(contractId);
                        } else {
                            this.appFacadeService.openContract(contractId);
                            this.notificationService.error(this.translateService.instant(error.message));
                        }

                        this.store.dispatch(setQuoteConversionInProgress({ inProgress: false }));
                        return EMPTY;
                    })
                );
            })
        )
    );

    private usableCustomerId(customerId: string | undefined): string {
        return customerId && customerId !== '0' ? customerId : '';
    }

    private prepareHeaders(pageNumber: number, customerId: string, status?: string[], startDate?: string, endDate?: string, search?: string, pageSize?: number): ApiHeaders {
        const columns = [
            'Id',
            'CustomerId',
            'Name',
            'Status',
            'ContractStatus',
            'StartDateTime',
            'EndDateTime',
            'PONumber',
            'AdditionalFields',
            'Addresses',
            'GrandTaxTotal',
            'GrandTotal',
            'AmountDue',
            'DepotId',
            'LineItems'
        ];
        let headers: ApiHeaders = {
            /* eslint-disable @typescript-eslint/naming-convention */
            'x-columns': JSON.stringify(columns)
        };

        if (pageNumber && customerId) {
            headers = {
                ...headers,
                /* eslint-disable @typescript-eslint/naming-convention */
                'x-paging': JSON.stringify({ page: pageNumber, pageSize: pageSize })
            };
        }

        const finalFilter = this.buildFilters(status, search, startDate, endDate);

        if (finalFilter.length > 0) {
            headers = {
                ...headers,
                /* eslint-disable @typescript-eslint/naming-convention */
                'x-filter': JSON.stringify(finalFilter)
            };
        }

        return headers;
    }

    private buildFilters(status?: string[], search?: string, startDate?: string, endDate?: string): unknown[] {
        const filters = [];

        if (this.appFacadeService.versionToggleService.isBasicVersion()) {
            filters.push({ field: 'Status', type: '!=', value: ContractFilterStatus.Closed });
            // AB#89333: Removed AmountDue > 0 filter to allow $0 balance contracts to display
            // Users need to see open contracts regardless of balance amount
        }

        if (search && !isEmpty(search)) {
            const searchFilters = [];

            if (this.appFacadeService.featureToggleService.isAvailable('textSearchMultipleFilters')) {
                searchFilters.push(
                    { field: 'PONumber', type: 'LIKE', value: search },
                    { field: 'AdditionalFields.CustomerJobNumber', type: 'LIKE', value: search },
                    { field: 'Name', type: 'LIKE', value: search }
                );

                // #94144 Syrinx - Id has to be number but sent as string and LIKE doesn't work
                if (this.appFacadeService.featureToggleService.isSyrinx()) {
                    if (!isNaN(Number(search))) {
                        searchFilters.push({ field: 'Id', type: '==', value: Number(search).toString() });
                    }
                } else {
                    searchFilters.push({ field: 'Id', type: 'LIKE', value: search });
                }
            } else {
                searchFilters.push({ field: 'Name', type: 'LIKE', value: search });
            }
            filters.push({ field: '', type: 'OR', value: searchFilters });
        }

        if (status && status?.length > 0) {
            const statusFilters = status?.map(statusItem => ({
                field: 'Status',
                type: '==',
                value: statusItem
            }));
            filters.push({ field: '', type: 'OR', value: statusFilters });
        }

        if (startDate) {
            filters.push({ field: 'StartDateTime', type: '>=', value: new Date(new Date(startDate).setUTCHours(0, 0, 0, 0)) });
        }
        if (endDate) {
            filters.push({ field: 'EndDateTime', type: '<=', value: new Date(new Date(endDate).setUTCHours(23, 59, 59, 999)) });
        }

        return filters;
    }
}
